import { useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconChevronDownOutline18 as ChevronDown,
	IconUserChangeOutline18 as SwitchIcon,
} from "nucleo-ui-outline-18"

import type { SessionData } from "@productlane/auth"
import { cn } from "@productlane/lib/src/cn"
import { Avatar, Button, Loader } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
} from "@productlane/ui/src/components/internal/dropdown"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { BAR_BUTTON, BAR_ICON } from "./bar-styles"
import { roleLabel } from "./impersonation-context"

export function ImpersonationMemberMenu({ session }: { session: SessionData }) {
	const [open, setOpen] = useState(false)
	const utils = api.useUtils()
	const membersQuery = api.user.impersonationMembers.useQuery(undefined, {
		enabled: open,
		staleTime: 30_000,
	})
	const switchMutation = api.user.impersonateUser.useMutation({
		onSuccess: async () => {
			await utils.user.info.reset()
			window.location.reload()
		},
		onError: (err) => {
			const show = err.data?.code === "NOT_FOUND" ? toast.info : toast.error
			show({ title: err.message, icon: SwitchIcon })
		},
	})
	const members = membersQuery.data ?? []

	return (
		<DropdownMenu
			open={open}
			onOpenChange={setOpen}
			contentProps={{ side: "top", align: "start", sideOffset: 8 }}
			className="w-80"
			trigger={
				<Button
					variant="ghost"
					size="sm"
					iconRight={ChevronDown}
					iconRightClassName={BAR_ICON}
					className={cn(BAR_BUTTON, "bg-white/40 pl-1")}
					isLoading={switchMutation.isLoading}
					wrapperClassName="gap-1.5"
					aria-label="Switch member"
				>
					<Avatar
						size="xxs+"
						name={session.name || session.email}
						image={session.imageUrl}
						className="flex-none"
					/>
					<span className="max-w-[10rem] truncate font-semibold">
						{session.name || session.email}
					</span>
					<span className="font-medium text-amber-800">
						{roleLabel(session.membershipRole)}
					</span>
				</Button>
			}
		>
			<DropdownMenuLabel>Switch member</DropdownMenuLabel>
			<DropdownMenuGroup className="max-h-80 overflow-y-auto pb-1">
				{membersQuery.isLoading ? (
					<div className="flex items-center justify-center py-3">
						<Loader />
					</div>
				) : (
					members.map((member) => {
						const isCurrent = member.id === session.userId
						return (
							<DropdownMenuItem
								key={member.id}
								IconRight={isCurrent ? Check : undefined}
								disabled={switchMutation.isLoading}
								onSelect={(event) => {
									if (isCurrent) {
										event.preventDefault()
										return
									}
									switchMutation.mutate({
										userId: member.id,
										workspaceId: session.workspaceId,
									})
								}}
							>
								<Avatar
									size="xs"
									name={member.name || member.email}
									image={member.imageUrl}
									className="mr-2 flex-none"
								/>
								<div className="flex min-w-0 flex-1 items-center justify-between gap-2 pr-2">
									<div className="min-w-0">
										<div className="truncate text-sm font-medium leading-tight">
											{member.name || member.email}
										</div>
										{member.name ? (
											<div className="truncate text-xs font-normal text-tertiary">
												{member.email}
											</div>
										) : null}
									</div>
									<span className="flex-none text-xs font-normal text-tertiary">
										{roleLabel(member.role)}
									</span>
								</div>
							</DropdownMenuItem>
						)
					})
				)}
			</DropdownMenuGroup>
		</DropdownMenu>
	)
}
